import { memo } from 'react'

import { formatDecimalPlaces } from '../../../helpers/functions/format-decimal-places'
import type { GameGoldMiner } from '../../../services/games/types'

interface Props {
  visible: boolean
  isLoading: boolean
  timesBet: number
  valueBet: number
  /** nome da moeda da jogada ('moedas' ou 'Blue Coins') */
  currency?: string
  gameGoldMinerData: GameGoldMiner[]
  handlePlay: () => void
  handleCloseModal: () => void
}

/**
 * Resultado do 10x/50x/100x — overlay CONTIDO no palco escalado (mesma caixa
 * das telas de regras/ranking), tamanho fixo: a grade rola se tiver muito
 * presente diferente, o resto não muda de lugar.
 */
export const ModalEarning = memo(
  ({
    visible,
    gameGoldMinerData,
    isLoading,
    timesBet,
    valueBet,
    currency = 'moedas',
    handlePlay,
    handleCloseModal,
  }: Props) => {
    if (!visible || isLoading || !gameGoldMinerData?.length) return null

    const total = gameGoldMinerData.reduce(
      (sum, g) => sum + (g.valueGift ?? 0) * (g.quantity ?? 1),
      0,
    )
    // presente mais valioso primeiro (pelo valor unitário)
    const sorted = [...gameGoldMinerData].sort((a, b) => b.valueGift - a.valueGift)

    return (
      <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/60 px-5 backdrop-blur-[2px]">
        <div
          className="gm-pop flex h-[400px] w-full flex-col rounded-2xl border-2 border-yellow-400/80 p-4 shadow-[0_0_30px_rgba(255,190,40,0.35)]"
          style={{
            background:
              'linear-gradient(180deg, #5b3517 0%, #3b2110 55%, #2a170b 100%)',
          }}
        >
          <div className="shrink-0 text-center">
            <p className="text-[11px] font-bold tracking-[0.2em] text-yellow-200/70 uppercase">
              Mineração {timesBet}x
            </p>
            <h3 className="text-xl font-black text-yellow-300 drop-shadow">
              Parabéns!
            </h3>
          </div>

          <div className="mt-3 min-h-0 flex-1 overflow-y-auto rounded-xl bg-black/30 p-2">
            <div className="grid grid-cols-4 gap-2">
              {sorted.map((g, i) => (
                <div
                  key={`${g.giftIdEarning}-${g.quantity}-${i}`}
                  className="gm-rise relative flex flex-col items-center rounded-lg border border-yellow-600/40 bg-gradient-to-b from-amber-900/70 to-black/40 px-1 pt-1.5 pb-1"
                  style={{ animationDelay: `${Math.min(i * 40, 400)}ms` }}
                >
                  {g.quantity > 1 && (
                    <span className="absolute top-0.5 right-1 rounded bg-yellow-400 px-1 text-[10px] font-black text-amber-950">
                      ×{g.quantity}
                    </span>
                  )}
                  <img
                    src={g.imgGift || g.imgGiftFrame}
                    alt={g.giftName}
                    className="h-12 w-12 object-contain"
                  />
                  <span className="mt-0.5 text-[11px] font-extrabold text-yellow-200 tabular-nums">
                    {/* valor UNITÁRIO — a quantidade fica no selo ×N em cima;
                        mostrar valor×qtd fazia o presente parecer valer mais */}
                    {formatDecimalPlaces(g.valueGift)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 flex shrink-0 items-center justify-center gap-2 text-sm font-bold text-yellow-100/80">
            Total
            <span className="text-2xl font-black text-yellow-300 tabular-nums">
              {formatDecimalPlaces(total)}
            </span>
          </div>

          <div className="mt-3 grid shrink-0 grid-cols-2 gap-2">
            <button
              type="button"
              onClick={handleCloseModal}
              className="h-11 rounded-full border border-yellow-200/40 bg-white/10 text-sm font-extrabold text-white active:scale-95"
            >
              Recolher
            </button>
            <button
              type="button"
              onClick={handlePlay}
              className="flex h-11 flex-col items-center justify-center rounded-full bg-gradient-to-b from-orange-400 to-orange-600 leading-tight text-white shadow-[0_3px_0_#9a3412] active:scale-95"
            >
              <span className="text-sm font-black">Jogar {timesBet}x</span>
              {valueBet > 0 && (
                <span className="text-[10px] font-bold text-yellow-100">
                  {formatDecimalPlaces(timesBet * valueBet)} {currency}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    )
  },
)

ModalEarning.displayName = 'ModalEarning'
